import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import { endOfToday } from "date-fns/esm";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useIntl } from "react-intl";

import {
  EntityType,
  EntityTypeSupportingSourceTracking,
} from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { LoadingSpinner } from "@mapmycustomers/ui";

import DashCard from "@app/component/DashCard";
import SelectField from "@app/component/input/SelectField";
import MoreButton from "@app/component/MoreButton";
import { SupportedSources } from "@app/enum/SourceType";
import { SIMPLE_DATE_RANGE_LABELS } from "@app/scene/home/component/Cards/util";
import ChartType from "@app/scene/home/enum/ChartType";
import { DisplayType } from "@app/scene/home/enum/DisplayType";
import {
  getRecordsBySourceSummary,
  getRecordsSourceFilter,
  isFunnelSummaryLoading,
} from "@app/scene/home/store";
import {
  fetchRecordsBySourceForSelectedType,
  fetchRecordsBySourceSummary,
  setRecordSourceFilter,
} from "@app/scene/home/store/actions";
import RecordsSourceFilter from "@app/scene/home/types/RecordsSourceFilter";
import getStartDate from "@app/scene/home/util/getStartDate";
import { RootState } from "@app/store/rootReducer";
import SourceSummary from "@app/types/source/SourceSummary";
import { formatDate } from "@app/util/formatters";

import { DEFAULT_CHART_CONFIG } from "./DefaultChartConfig";
import { displayTypeMessages, generalMessages } from "./messages";
import RecordListBySourceModal from "./RecordListBySourceModal";
import RecordTypesModal from "./RecordTypesModal";
import styles from "./SourceCard.module.scss";
import { SourceTypesModal } from "./SourceTypesModal";
import useSourceTypeChartConfig from "./useSourceTypeChartConfig";

const chartStyles = {
  width: "100%",
};

interface Props {
  className?: string;
  fetchRecordsBySourceForSelectedType: typeof fetchRecordsBySourceForSelectedType.request;
  fetchRecordSource: typeof fetchRecordsBySourceSummary.request;
  filter: RecordsSourceFilter;
  isLoading: boolean;
  recordSourceSummary: SourceSummary[];
  selectedUsers: User[];
  setFilter: typeof setRecordSourceFilter;
}

const RecordSource: React.FC<Props> = ({
  className,
  fetchRecordsBySourceForSelectedType,
  fetchRecordSource,
  filter,
  isLoading,
  recordSourceSummary,
  selectedUsers,
  setFilter,
}) => {
  const intl = useIntl();
  const { displayType, recordTypes, sourceTypes } = filter;
  const [sourceDialogVisible, showSourceDialog, hideSourceDialog] = useBoolean();
  const [selectedSourceData, showSelectedSourceData, hideSelectedSourceData] = useBoolean();
  const [selectedSourceType, setSelectedSourceType] = useState<SupportedSources | undefined>();

  const [activityTypesDialogVisible, showActivityTypesDialog, hideActivityTypesDialog] =
    useBoolean();

  const [dateRange, setDateRange] = useState(
    SIMPLE_DATE_RANGE_LABELS.length ? SIMPLE_DATE_RANGE_LABELS[0].value : ""
  );
  const startDate = getStartDate(dateRange);
  const setRecordTypes = useCallback(
    (recordTypes: Exclude<EntityTypeSupportingSourceTracking, EntityType.ACTIVITY>[]) =>
      setFilter({ ...filter, recordTypes }),
    [filter, setFilter]
  );

  const toggleDisplayType = useCallback(
    () =>
      setFilter({
        ...filter,
        displayType: displayType === DisplayType.PERCENT ? DisplayType.GROSS : DisplayType.PERCENT,
      }),
    [displayType, filter, setFilter]
  );

  const setSourceTypes = useCallback(
    (sourceTypes: SupportedSources[]) => setFilter({ ...filter, sourceTypes }),
    [filter, setFilter]
  );

  useEffect(() => {
    fetchRecordSource({
      dateRange,
      entityTypes: recordTypes,
      userId: selectedUsers.map(({ id }) => id),
    });
  }, [dateRange, fetchRecordSource, recordTypes, selectedUsers]);

  const handleChangeDateRange = useCallback(
    (value) => {
      setDateRange(value);
    },
    [setDateRange]
  );

  const title = useMemo(() => {
    return (
      <>
        <h2>
          {intl.formatMessage(
            {
              id: "recordsBySource.modal.title",
              defaultMessage: "Records Created by {source}",
              description: "Records by source list modal title",
            },
            {
              source: selectedSourceType,
            }
          )}
        </h2>
        <h6 className={styles.subtitle}>
          {startDate && `${formatDate(startDate, "PP")} — ${formatDate(endOfToday(), "PP")}`}
        </h6>
      </>
    );
  }, [selectedSourceType, intl, startDate]);

  const handleSelectedType = useCallback(
    (type: SupportedSources) => {
      setSelectedSourceType(type);
      fetchRecordsBySourceForSelectedType({
        dateRange,
        entityTypes: recordTypes,
        sourceType: type,
        userId: selectedUsers.map(({ id }) => id),
      });
      showSelectedSourceData();
    },
    [
      dateRange,
      fetchRecordsBySourceForSelectedType,
      recordTypes,
      selectedUsers,
      setSelectedSourceType,
      showSelectedSourceData,
    ]
  );

  const options = useSourceTypeChartConfig(
    DEFAULT_CHART_CONFIG,
    displayType,
    handleSelectedType,
    filter.sourceTypes,
    recordSourceSummary,
    intl
  );

  return (
    <DashCard
      actions={[
        <SelectField<string>
          className={styles.dateField}
          key="date-range"
          label={intl.formatMessage({
            id: "home.cards.recordsBySource.selectField.label",
            defaultMessage: "Date Range",
            description: "Label for Select Field in Activity by Types Card",
          })}
          labelClassName={styles.filterLabel}
          onChange={handleChangeDateRange}
          options={SIMPLE_DATE_RANGE_LABELS}
          value={dateRange}
        />,
        <Dropdown
          key="menu"
          overlay={
            <Menu>
              <Menu.Item key="recordTypes" onClick={() => showActivityTypesDialog()}>
                {intl.formatMessage({
                  id: "home.cards.recordsBySource.dropdown.menuItem.selectRecordTypes",
                  defaultMessage: "Select record types",
                  description: "Title for the select activity types menu item",
                })}
              </Menu.Item>
              <Menu.Item key="sourceTypes" onClick={() => showSourceDialog()}>
                {intl.formatMessage({
                  id: "home.cards.recordsBySource.dropdown.menuItem.selectSources",
                  defaultMessage: "Select Sources",
                  description: "Title for the select sources menu item",
                })}
              </Menu.Item>
              <Menu.Item key="displayType" onClick={toggleDisplayType}>
                {intl.formatMessage(displayTypeMessages[displayType])}
              </Menu.Item>
            </Menu>
          }
          placement="bottomRight"
          trigger={["click"]}
        >
          <MoreButton />
        </Dropdown>,
      ]}
      className={className}
      title={intl.formatMessage({
        id: "home.cards.recordsBySource.title",
        defaultMessage: "Records by Source",
        description: "Record Source Card Title",
      })}
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <HighchartsReact
          containerProps={{ style: chartStyles }}
          highcharts={Highcharts}
          isPureConfig
          options={options}
        />
      )}
      <RecordTypesModal
        onConfirm={setRecordTypes}
        onHide={hideActivityTypesDialog}
        selectedEntities={recordTypes}
        title={intl.formatMessage(generalMessages.recordTypesDialogTitle)}
        visible={activityTypesDialogVisible}
      />
      <SourceTypesModal
        onConfirm={setSourceTypes}
        onHide={hideSourceDialog}
        selectedEntities={sourceTypes}
        title={intl.formatMessage(generalMessages.sourceTypesDialogTitle)}
        visible={sourceDialogVisible}
      />
      {selectedSourceData && (
        <RecordListBySourceModal
          chartType={ChartType.SOURCE}
          dateRange={dateRange}
          onHide={() => hideSelectedSourceData()}
          pageSize={25}
          recordTypes={recordTypes}
          sourceType={selectedSourceType}
          title={title}
          users={selectedUsers}
        />
      )}
    </DashCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  filter: getRecordsSourceFilter(state),
  isLoading: isFunnelSummaryLoading(state),
  recordSourceSummary: getRecordsBySourceSummary(state),
});

const mapDispatchToProps = {
  fetchRecordsBySourceForSelectedType: fetchRecordsBySourceForSelectedType.request,
  fetchRecordSource: fetchRecordsBySourceSummary.request,
  setFilter: setRecordSourceFilter,
};

export default connect(mapStateToProps, mapDispatchToProps)(RecordSource);
